import { ClaimV2CornSchema, PublicationTargetsSchema } from '@eigtech/claims-v2-types';
import { NoteCornSchema } from '@eigtech/notes-types';
import { z } from 'zod';
import { DocumentCornSchema, InspectionCornSchema, InspectionEntityCornSchema, VideoCornSchema, } from '../base.js';
/**
 * Individual units of work performed once an inspection is completed. Each name maps to a key in
 * `InspectionCompletionProcessing.steps` tracking whether that unit last succeeded or failed.
 */
export const InspectionCompletionStepNameSchema = z.enum([
    'videoConcat',
    'notes',
    'resolveContext',
    'notePublication',
    'transcript',
    'entityMediaUpload',
    'entityMediaPublication',
    'claimMediaUpload',
    'claimMediaPublication',
    'summarize',
]);
export const InspectionCompletionStepNameEnum = InspectionCompletionStepNameSchema.enum;
export const InspectionCompletionStepStatusSchema = z.enum([
    'succeeded',
    'failed',
    'in_progress',
]);
export const InspectionCompletionStepStatusEnum = InspectionCompletionStepStatusSchema.enum;
export const InspectionCompletionStepStateSchema = z.object({
    status: InspectionCompletionStepStatusSchema,
    error: z.string().optional(),
    /** Set while `in_progress`; after this time another execution may steal the lease. */
    leaseExpiresAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime(),
});
/**
 * Idempotency and status record for post-completion processing of a single inspection. A step's
 * absence from `steps` means it has not been attempted yet (either not reached, or deferred
 * pending video processing). `in_progress` is a short-lived lease so concurrent executions cannot
 * both perform the same external side effects; `succeeded`/`failed` are terminal until retry.
 *
 * IDs created in external services (Notes, Documents) are cached here so retries never recreate
 * them; `*UploadedDocumentIds`/`*PublishedDocumentIds` key uploads/publications by the source
 * inspection media corn (document/image ID) so individual media items can be retried in isolation.
 */
export const InspectionCompletionProcessingSchema = z.object({
    entityId: InspectionEntityCornSchema,
    inspectionId: InspectionCornSchema,
    /** Notes-service note combining the inspection's scope notes (everything but the summary). */
    noteId: NoteCornSchema.optional(),
    /** Notes-service note holding the inspection summary, kept separate from the scope notes. */
    summaryNoteId: NoteCornSchema.optional(),
    transcriptDocumentId: DocumentCornSchema.optional(),
    /** MediaConvert Job.Id for the full-inspection concat; set when videoConcat kicks off the job. */
    videoConcatJobId: z.string().optional(),
    /** Full-inspection video corn minted when the concat job is started. */
    videoConcatVideoId: VideoCornSchema.optional(),
    linkedClaimEntityId: ClaimV2CornSchema.optional(),
    /** Resolved by the `resolveContext` step; consumed by notePublication and media publication. */
    externalPublicationTargets: PublicationTargetsSchema.array().default([]),
    entityUploadedDocumentIds: z.record(z.string(), z.string()).default({}),
    entityPublishedDocumentIds: z.array(z.string()).default([]),
    claimUploadedDocumentIds: z.record(z.string(), z.string()).default({}),
    claimPublishedDocumentIds: z.array(z.string()).default([]),
    steps: z
        .partialRecord(InspectionCompletionStepNameSchema, InspectionCompletionStepStateSchema)
        .default({}),
    updatedAt: z.iso.datetime(),
});
